/**
 * Step 3: Answers - User answers business questions
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useWizard, normalizeQuestion, isAnswerEmpty } from './WizardContext';
import GenerationLock from './GenerationLock';
import { ReadOnlyStepBanner } from './WizardBanners';
import RequirementContextCard from './RequirementContextCard';
import SmartAnswerInput, { answerLayoutWidth, resolveAnswerControl } from '@/components/SmartAnswerInput';
import {
  orderQuestionsForDisplay,
  segmentOrderedQuestions,
  controlWidthClass,
  BUDGET_FOOTNOTE,
} from '@/lib/questionDisplay';
import { autoResizeTextarea, TEXTAREA_BASE_ROWS, GROWING_TEXTAREA_CLASS } from '@/lib/autoResizeTextarea';
import FieldError from '@/components/FieldError';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, Loader2, AlertTriangle, AlertCircle, DollarSign, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toastError } from '@/lib/toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from '@/lib/api';
import {
  getRequiredDateFieldKey,
  findAnswerByFieldKey,
  findMissingMandatoryFieldKeys,
  checkAnswersTemporalValidity,
  parseLegacyBudget,
  toIsoDateOrNull,
} from '@/lib/answersValidation';
import {
  parseQuantityText,
  formatQuantityComposite,
  splitQuantityComposite,
  filterUomsForType,
  defaultUomForType,
  CALENDAR_DURATION_UNITS,
  deriveCalendarQuantityFromDates,
} from '@/lib/quantityComposite';
import { appendRuleCodeSuffix, formatRuleCodeSuffix, SOFT_ALERT_SEVERITY } from '@/lib/dateValidationRules';
import {
  boundStatedVolume,
  matchStatedUnitAgainstMaster,
  checkStatedVolumeEcho,
  checkStatedDurationEcho,
  checkStatedUnitDimensionEcho,
  sardAttributionForQuantityMismatch,
} from '@/lib/statedIntentValidationRules';
import { formatDate } from '@/lib/utils';
import { getLocationProposal } from '@/components/ParsedRequirementFields';
import SeverityChip from '@/components/SeverityChip';

// Issue 19 (ruled 2026-07-19): a free-text "Any other suggestion" field,
// non-mandatory, appended after the AI-generated business questions - not
// itself a business question (never sent through finalize-questions, no
// MANDATORY badge, no agent prompt changes). Kept OUTSIDE the q{i}
// positional answers scheme (WizardContext's reconcileAnswersForQuestions/
// handleRemoveBusinessQuestion both preserve non-q{i} keys) so it survives
// a Questions-step edit/regeneration untouched. Still lands in the same
// `answers` object submitted to submit-answers, so it persists into
// user_answers exactly like any other answer and reaches refinement_agent
// via the same json.dumps(user_answers) the prompt already does.
const OTHER_SUGGESTION_KEY = 'additional_suggestion';
const OTHER_SUGGESTION_QUESTION = 'Any other suggestion or context you\'d like to add?';

// Plain-text display for a business question's saved answer - location
// answers may be an object (see extractLocationAnswer's location_id shape).
const formatSavedAnswer = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'object') return value.location_id || JSON.stringify(value);
  return String(value);
};

// PRESENCE MESSAGES, per mandatory field_key - the same strings the
// submit-time toasts used to carry, now rendered under the field and in
// the summary above Continue (Ruling 3, 2026-09-03). Any other mandatory
// question (a generated one flagged is_mandatory without a dedicated
// field_key) gets the generic line.
const PRESENCE_MESSAGES = {
  ship_to_location: 'Please select a ship-to / delivery location',
  quantity: 'Please provide the quantity',
  rfp_currency: 'Please select the RFP currency',
  budget: 'Please provide the estimated budget',
  need_by_date: 'Please provide the required delivery date',
  start_date: 'Please provide the service start date',
  end_date: 'Please provide the service end date',
  submission_deadline: 'Proposal submission deadline is required',
};
const GENERIC_PRESENCE_MESSAGE = 'This question is required';

// WHICH QUESTIONS BLOCK CONTINUE - and why this is NOT the order helper's
// mandatory predicate.
//
// The display order (lib/questionDisplay.js orderQuestionsForDisplay)
// decides the MANDATORY badge and which group a question renders in: the
// type-appropriate field_key set OR the stored is_mandatory flag - the
// badge's own pre-existing predicate.
//
// isPresenceRequired decides which unanswered questions BLOCK Continue. It
// reproduces, exactly, the set the submit-time checks enforced before the
// 2026-09-03 slices: the four always-required keys and the type's required
// date(s) are required whenever the question EXISTS (flag ignored);
// quantity is required iff the procurement is not services (flag ignored,
// BOQ ignored - as before); end_date only for services; everything else by
// its flag.
//
// They differ on a services quantity question that carries is_mandatory:
// true (badged, but not blocking - the flag is forced false by the backend
// for services, so this is a legacy-document edge) and on a goods quantity
// with a BOQ present. Unifying them would change what makes a question
// mandatory, which is ruled out of scope (the services-quantity question is
// a separate ruling). Not built here.
const isPresenceRequired = (nq, procurementType) => {
  const key = nq.field_key;
  if (key === 'quantity') return procurementType !== 'services';
  if (['ship_to_location', 'rfp_currency', 'budget', 'submission_deadline'].includes(key)) return true;
  if (key === getRequiredDateFieldKey(procurementType)) return true;
  if (key === 'end_date') return procurementType === 'services';
  return Boolean(nq.is_mandatory);
};

// Every mandatory question whose answer is empty, in stored order, with
// the message to show under it. Derived LIVE from the current answers (not
// snapshotted at the failed click) so an error disappears the moment the
// buyer fills the field, and the summary above Continue shrinks with it -
// and so a jump link, which changes no state, cannot dismiss either.
export const findUnansweredMandatoryQuestions = (businessQuestions, answers, procurementType) => {
  const missing = [];
  (businessQuestions || []).forEach((q, index) => {
    const nq = normalizeQuestion(q);
    if (!isPresenceRequired(nq, procurementType)) return;
    if (!isAnswerEmpty((answers || {})[`q${index}`])) return;
    missing.push({
      index,
      fieldKey: nq.field_key,
      question: nq.question,
      message: PRESENCE_MESSAGES[nq.field_key] || GENERIC_PRESENCE_MESSAGE,
    });
  });
  return missing;
};

// Scroll a question into view and put focus on its first control. State-
// free by design (Ruling 3): the summary the buyer clicked stays rendered
// because nothing here touches what renders it.
const jumpToQuestion = (index) => {
  const item = document.getElementById(`answer-item-${index}`);
  if (!item) return;
  if (typeof item.scrollIntoView === 'function') item.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const control = item.querySelector('input, textarea, select, button, [role="combobox"]');
  if (control && typeof control.focus === 'function') control.focus({ preventScroll: true });
};

// Group headings + the break between them. `optional` gets a top border
// so the two groups read as two, not one long list (mutant AR4).
const GroupHeading = ({ kind, count }) => (
  <div
    className={kind === 'optional' ? 'border-t border-zinc-200 pt-6 mt-2' : ''}
    data-testid={kind === 'optional' ? 'question-group-break' : 'question-group-required-heading'}
  >
    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
      {kind === 'optional' ? `Optional questions (${count})` : `Required questions (${count})`}
    </h3>
    {kind === 'optional' && (
      <p className="text-xs text-zinc-500 mt-1">Answer what you can - these refine the RFP but do not block it.</p>
    )}
  </div>
);

const StepAnswers = () => {
  const {
    loading,
    businessQuestions,
    answers,
    setAnswers,
    setCurrentStep,
    handleAnswersSubmit,
    handleUploadsComplete,
    confirmAdvanceToLineItems,
    consistencyValidation,
    setSelectedCurrency,
    getProcurementType,
    temporalBlock,
    setTemporalBlock,
    postWizardEvent,
    isStepPreviouslyCompleted,
    isStepUnlocked,
    unlockStepForEdit,
    markStepDirty,
    procurementId,
    userRequirement,
    parsedRequirement,
    echoMetadata,
    boqItems
  } = useWizard();

  const procurementType = getProcurementType();
  // PROC-000026 follow-up (16fd26c frontend mirror): same boq_present
  // definition as the backend's _missing_mandatory_field_keys_check - a
  // non-empty boq_items list. boqItems is populated by loadProcurement on
  // resume (persisted state) as well as a same-session upload, so a
  // resumed BOQ procurement is never wrongly re-blocked here.
  const boqPresent = Boolean(boqItems && boqItems.length > 0);
  // Item 7: a temporal hard block always forces the editable form, even if
  // Answers was previously completed - the whole point is the user must fix
  // a now-stale date, which a read-only view wouldn't let them do.
  const isReadOnly = (isStepPreviouslyCompleted('answers') && !isStepUnlocked('answers')) && !temporalBlock;

  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  // PROC-000054 item 1 (revised): quantity's composite unit dropdown and
  // its submit-time unit-from-master validation both need uom_master -
  // same fetch-here-too pattern as currencies/locations above (their own
  // components self-fetch independently as well, for their own dropdowns -
  // see LocationDropdown/QuantityCompositeInput in SmartAnswerInput.jsx).
  const [uoms, setUoms] = useState([]);
  const [loadingUoms, setLoadingUoms] = useState(true);
  // B2-B (2026-07-30): budget validation moves from a blocking toastError
  // to inline field validation, per the Error Message Taxonomy ruling
  // (user-actionable -> point-of-use, not a toast) - same FieldError
  // component LineItemsEditor.jsx already uses. Cleared the moment the
  // user edits the field (see the budget onChange below), not just on a
  // fresh Continue attempt - a corrected value's error disappears
  // immediately rather than waiting for another click.
  const [budgetError, setBudgetError] = useState(null);
  // Ruling 3 (2026-09-03): once a Continue click has found a mandatory
  // question unanswered, every unanswered mandatory question is marked
  // under its own control AND listed in a summary directly above the
  // Back/Continue row. Set true by the failed click, never set back -
  // the list itself is derived live from `answers`, so it empties (and
  // the summary disappears) as the buyer fills the fields.
  const [showMissingMandatory, setShowMissingMandatory] = useState(false);
  // Warning C (required date vs TODAY - SANT/SAST, HIGH) - soft-alert
  // severity batch (2026-07-26): surfaced at Answers now (previously
  // deferred to a future admin-parameters batch); highest severity, so
  // always first in the chain when it fires.
  const [showRequiredSoonWarning, setShowRequiredSoonWarning] = useState(false);
  const [requiredSoonDetails, setRequiredSoonDetails] = useState(null);
  // Warning A (deadline vs TODAY - SAPT, LOW) - moved here from Lines
  // (deadline-warning allocation, 2026-07-22): the deadline is entered at
  // THIS step and doesn't change downstream, so this is where the check
  // belongs.
  const [showDeadlineSoonWarning, setShowDeadlineSoonWarning] = useState(false);
  const [deadlineSoonDetails, setDeadlineSoonDetails] = useState(null);
  // Warning B (deadline vs required/start date - SAPN/SAPS, MEDIUM) -
  // answers-step batch (2026-07-22): moved back to Answers per the "all
  // deadline checks belong at Answers" ruling, since the required date is
  // answered here. Lines keeps its own hard+soft check as a backstop
  // against a line date later edited away from this headline value (item 4).
  const [showRunwayWarning, setShowRunwayWarning] = useState(false);
  const [runwayDetails, setRunwayDetails] = useState(null);
  // The remaining not-yet-shown warnings for THIS submit attempt, most-
  // severe-first, each {kind: 'runway'|'today', details} - lets whichever
  // dialog is showing chain straight into the next without re-running
  // handleNextClick. Purely a within-attempt relay: never persisted,
  // always overwritten fresh by the next submit click's evaluation (or
  // left unread if the user Goes Back instead of Proceeding - see
  // handleNextClick's comment for the full acknowledgment + severity-
  // ordering semantics).
  const [pendingChain, setPendingChain] = useState([]);
  // Item B(ii) (duration/dates cooperation, services only): soft, non-
  // blocking confirm when a calendar-duration quantity (Month/Week)
  // disagrees with what start/end implies. Evaluated fresh on every submit
  // click, same acknowledgment convention as the date soft-warnings above -
  // no per-procurement "already shown" guard.
  const [showQuantityDateMismatch, setShowQuantityDateMismatch] = useState(false);
  const [quantityDateMismatchDetails, setQuantityDateMismatchDetails] = useState(null);
  // Stated-intent echo checks (Ashok ruling, 2026-07-25): SARV (volume) and
  // SARD (duration) - same per-attempt chain/proceed-anyway convention as
  // the date soft-warnings above, joined via pendingChain (see
  // runChecksAfterQuantity). See lib/statedIntentValidationRules.js.
  const [showSarvWarning, setShowSarvWarning] = useState(false);
  const [sarvDetails, setSarvDetails] = useState(null);
  const [showSardWarning, setShowSardWarning] = useState(false);
  const [sardDetails, setSardDetails] = useState(null);
  // SARU (MIXED-SEMANTICS + ECHO FIX BATCH, item D) - cross-dimension unit
  // echo, same per-attempt chain convention, ALL procurement types.
  const [showSaruWarning, setShowSaruWarning] = useState(false);
  const [saruDetails, setSaruDetails] = useState(null);
  const [showConsistencyWarning, setShowConsistencyWarning] = useState(false);
  // Item 7: one-time explaining dialog for a temporal hard block set by
  // WizardContext (resume-time revalidation or the RFP-generation gate) -
  // opens once per arrival, but the persistent banner below stays up for as
  // long as temporalBlock itself is set, dialog dismissed or not.
  const [showTemporalBlockDialog, setShowTemporalBlockDialog] = useState(false);
  // Item 3 (consolidated fix batch, 2026-07-24): only these codes describe a
  // date that was genuinely fine when set and has since slipped into the
  // past (caught by resume-time revalidation or the gate). Relationship
  // codes (HVPN/HVPS/HVES) can be reached via the SAME resume/gate paths but
  // don't mean a date "passed" - they mean two dates no longer relate
  // correctly - so they get a neutral frame instead, below.
  const PAST_DATE_TEMPORAL_CODES = ['HVPT', 'HVND', 'HVSD'];
  const isPastDateTemporalBlock = !!(temporalBlock && PAST_DATE_TEMPORAL_CODES.includes(temporalBlock.code));

  useEffect(() => {
    if (temporalBlock) {
      setShowTemporalBlockDialog(true);
    }
  }, [temporalBlock]);

  // Backend-detected non-blocking warnings (from the submit-answers
  // consistency check) must also block until the user explicitly chooses,
  // same as the client-side deadline-gap dialog below - previously these
  // only fired a toast and the wizard advanced regardless.
  useEffect(() => {
    const issues = consistencyValidation?.issues || [];
    const hasErrors = issues.some(i => i.severity === 'error');
    const hasWarnings = issues.some(i => i.severity === 'warning');
    if (hasWarnings && !hasErrors) {
      setShowConsistencyWarning(true);
    }
  }, [consistencyValidation]);

  // Pre-populate answers with AI-supplied default_value where the answer is
  // still empty (e.g. quantity/location already stated in the requirement text).
  useEffect(() => {
    setAnswers(prev => {
      const updated = { ...prev };
      let changed = false;
      businessQuestions.forEach((q, i) => {
        const nq = normalizeQuestion(q);
        const key = `q${i}`;
        if (nq.default_value && !updated[key]) {
          updated[key] = nq.default_value;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [businessQuestions, setAnswers]);

  // Item 5: normalize a legacy TEXT budget answer (a procurement in flight
  // before the numeric input shipped) to a clean numeric string on load, so
  // the numeric <input> and the eventually-submitted `answers` payload agree
  // - not just the display.
  useEffect(() => {
    const budgetIdx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'budget');
    if (budgetIdx === -1) return;
    const key = `q${budgetIdx}`;
    setAnswers(prev => {
      const raw = prev[key];
      if (raw === undefined || raw === null || raw === '') return prev;
      const cleaned = parseLegacyBudget(raw);
      if (cleaned === String(raw)) return prev;
      return { ...prev, [key]: cleaned };
    });
  }, [businessQuestions, setAnswers]);

  // PROC-000054 item 1d (revised): normalize a quantity answer that predates
  // the composite input - full prose ("need for 3 months from 1 Aug to 31st
  // October") from before ANY quantity fix, or a bare number ("10") from the
  // prior numeric-only fix - into the canonical "{number} {Unit}" form on
  // load/regenerate, mirroring the budget normalization above. Gated on
  // uoms having loaded (unit-word matching needs the master to match
  // against); parseQuantityText is a no-op re-composition on an
  // already-canonical value (e.g. "3 Month" -> value "3", uom "Month" ->
  // recomposes to "3 Month", same string, no rewrite), so this never fights
  // the user's own edits or the prefill effect below.
  useEffect(() => {
    if (loadingUoms) return;
    const quantityIdx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'quantity');
    if (quantityIdx === -1) return;
    const key = `q${quantityIdx}`;
    setAnswers(prev => {
      const raw = prev[key];
      if (raw === undefined || raw === null || raw === '') return prev;
      const { value, uom } = parseQuantityText(raw, uoms);
      const cleaned = formatQuantityComposite(value, uom);
      if (cleaned === String(raw)) return prev;
      return { ...prev, [key]: cleaned };
    });
  }, [businessQuestions, uoms, loadingUoms, setAnswers]);

  // PROC-000054 item 1c (Ashok's ruling - confirm, don't re-ask): seed the
  // quantity composite from the ORIGINAL parsed_requirement.quantity text
  // when the answer is still empty, so the user confirms/corrects a value
  // already implied by their own requirement instead of re-typing it from
  // scratch. A prefill is NOT an answer - isAnswerEmpty/submit validation
  // below still runs unchanged on whatever ends up in the field. Runs after
  // the legacy-normalize effect above (both gated on uoms loaded) so a
  // genuinely-empty answer is what's left to seed by the time this checks;
  // an unmatched unit falls back to the goods default ("Each") the same way
  // the composite input's own display fallback does, so a fresh goods
  // prefill lands fully answered rather than half-done.
  //
  // GOODS/MIXED ONLY (item B(i), services-engagement-models follow-up):
  // quantity is now optional for services (item A) - proactively seeding
  // SOMETHING from parsed text on every load would undermine that; a
  // services engagement may genuinely have no count at all. The one
  // services prefill that remains is narrower and interaction-driven: the
  // composite input itself derives a starting value from the engagement's
  // own start/end dates, but only when the user explicitly picks a
  // calendar-duration unit (Month/Week) - see QuantityCompositeInput in
  // SmartAnswerInput.jsx.
  useEffect(() => {
    if (loadingUoms || procurementType === 'services') return;
    const quantityIdx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'quantity');
    if (quantityIdx === -1) return;
    const key = `q${quantityIdx}`;
    setAnswers(prev => {
      if (!isAnswerEmpty(prev[key])) return prev;
      // ECHO_METADATA (item 6, 2026-07-26): prefer the structured stated
      // quantity+unit (single-intent echo_metadata) over the legacy prose
      // parse below - unambiguous, and immune to parsed_requirement's own
      // wrapper/nesting quirks. Falls back to parsing parsed_requirement.
      // quantity when echo_metadata is absent/malformed/multi-intent
      // (legacy docs, or a requirement echo_metadata couldn't bind to a
      // single intent) - same prefill behavior as before this field existed.
      const stated = boundStatedVolume(echoMetadata);
      if (stated) {
        const resolvedUnit = matchStatedUnitAgainstMaster(stated.rawUnitWord, uoms)
          || defaultUomForType(procurementType, filterUomsForType(uoms, procurementType));
        const seeded = formatQuantityComposite(stated.value, resolvedUnit);
        if (seeded) return { ...prev, [key]: seeded };
      }
      const parsedQuantityText = parsedRequirement?.quantity;
      if (!parsedQuantityText) return prev;
      const { value, uom } = parseQuantityText(parsedQuantityText, uoms);
      if (!value) return prev;
      const resolvedUom = uom || defaultUomForType(procurementType, filterUomsForType(uoms, procurementType));
      const seeded = formatQuantityComposite(value, resolvedUom);
      if (!seeded) return prev;
      return { ...prev, [key]: seeded };
    });
  }, [businessQuestions, parsedRequirement, echoMetadata, uoms, loadingUoms, procurementType, setAnswers]);

  // Item 1 (unit default from stated volume, defect fix - screenshot
  // evidence: "How many SAP consultants are required?" rendered with its
  // unit stuck on Month, so typing 4 produced "4 Months" and fired the
  // wrong (duration) check). SERVICES ONLY, complementary to
  // QuantityCompositeInput's own display-only statedUnit fallback below
  // (which only ever applies to a genuinely EMPTY unit): this effect
  // actively CLEARS an already-stored calendar unit (Month/Week) whenever
  // parsed_requirement.quantity states a non-calendar volume - a services
  // quantity question is never inherently a duration question, so a
  // calendar unit can't have survived here as the user's own deliberate
  // choice for THIS stated volume. Preserves whatever number is already
  // typed - only the unit was wrong. Division of labor (see
  // VALIDATION-FRAMEWORK.md): duration consistency is checked from
  // start/end dates (SARD); volume consistency from the quantity answer
  // (SARV) - the composite itself must never steer a volume question into
  // a calendar unit.
  useEffect(() => {
    if (loadingUoms || procurementType !== 'services') return;
    const quantityIdx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'quantity');
    if (quantityIdx === -1) return;
    // ECHO_METADATA rebase (2026-07-26): sourced from the intake agent's
    // structured stated-intent capture instead of a prose re-parse of
    // parsed_requirement.quantity - null on a legacy doc, malformed
    // echo_metadata, a multi-intent requirement (no single bound anchor),
    // or no stated quantity at all.
    const statedVolume = boundStatedVolume(echoMetadata);
    // Requires an actual unit WORD in the stated text (e.g. "consultants")
    // - a bare stated number with no unit signal at all isn't a strong
    // enough claim to justify overriding whatever calendar unit the
    // user/derivation logic already selected.
    if (!statedVolume?.rawUnitWord) return;
    const matchedUnit = matchStatedUnitAgainstMaster(statedVolume.rawUnitWord, uoms);
    const key = `q${quantityIdx}`;
    setAnswers(prev => {
      const { numberPart, unitPart } = splitQuantityComposite(prev[key]);
      if (!unitPart || !CALENDAR_DURATION_UNITS.includes(unitPart)) return prev; // nothing to correct
      const newUnit = matchedUnit || ''; // no safe match -> leave unselected rather than stuck on a calendar unit
      if (unitPart === newUnit) return prev;
      return { ...prev, [key]: formatQuantityComposite(numberPart, newUnit) };
    });
  }, [businessQuestions, echoMetadata, uoms, loadingUoms, procurementType, setAnswers]);

  // PROC-000054 item 1c ("similarly seed start_date..."): the parsed intake
  // schema (backend/prompts/intake_agent.txt) has no dedicated timeline/date
  // field today - category/quantity/scope/objective/procurement_type only -
  // so this checks a few plausible keys defensively and is a documented
  // no-op until the intake schema actually carries one. toIsoDateOrNull
  // (the same date-format guard used across dateValidationRules) means a
  // non-date value under one of these keys is silently ignored rather than
  // seeding garbage into the date picker.
  useEffect(() => {
    const requiredDateFieldKey = getRequiredDateFieldKey(procurementType);
    if (requiredDateFieldKey !== 'start_date') return;
    const startIdx = businessQuestions.findIndex(q => normalizeQuestion(q).field_key === 'start_date');
    if (startIdx === -1) return;
    const parsedTimelineValue =
      parsedRequirement?.start_date || parsedRequirement?.timeline?.start_date || parsedRequirement?.timeline;
    if (!parsedTimelineValue) return;
    const iso = toIsoDateOrNull(parsedTimelineValue);
    if (!iso) return;
    const key = `q${startIdx}`;
    setAnswers(prev => (isAnswerEmpty(prev[key]) ? { ...prev, [key]: iso } : prev));
  }, [businessQuestions, parsedRequirement, procurementType, setAnswers]);

  // Fetch UoMs on mount (item 1a/1c/1f: composite quantity's unit dropdown,
  // prefill/legacy-normalize unit matching, and submit-time unit-from-master
  // validation). VERIFICATION FIX (post-596050b): this feeds SARU
  // (checkStatedUnitDimensionEcho below needs measurement_family/
  // unit_semantics for every buyer, admin or not) - must use the
  // validation-purpose fetch, never the admin-gated browse one, or a
  // non-admin buyer's SARU check loses its same-measurement-family
  // silence and fires on every unit change.
  useEffect(() => {
    const fetchUoms = async () => {
      try {
        const response = await api.getMasterUomsForValidation();
        setUoms(response.data || []);
      } catch (error) {
        console.error('Failed to fetch UoMs:', error);
      } finally {
        setLoadingUoms(false);
      }
    };
    fetchUoms();
  }, []);

  // Fetch currencies on mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await api.getMasterCurrencies('selection');
        setCurrencies(response.data || []);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
  }, []);

  // Fetch locations on mount (item 6: validate the ship_to_location answer
  // against the location master, mirroring LineItemsEditor.jsx's pattern).
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.getLocations();
        setLocations(response.data.locations || response.data || []);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // Handle currency selection - also update the RFP currency
  const handleCurrencyChange = (questionIndex, value) => {
    setAnswers(prev => ({ ...prev, [`q${questionIndex}`]: value }));
    setSelectedCurrency(value);  // This becomes the RFP currency
    markStepDirty('answers');
  };

  // Re-derives the quantity composite's DISPLAY value from whatever's
  // stored, same "not just the effect - also the display" belt-and-
  // suspenders as parseLegacyBudget above. Gated on uoms having loaded: a
  // fuzzy re-parse before then would match nothing (empty master) and
  // transiently drop an already-good unit from an already-canonical value
  // ("3 Month" -> "3") for the instant between mount and the fetch
  // resolving - showing the raw stored value unchanged until then avoids
  // that flicker. QuantityCompositeInput itself never re-parses fuzzily -
  // it only strictly splits whatever this hands it.
  const displayQuantityValue = (raw) => {
    if (loadingUoms) return raw || '';
    const { value, uom } = parseQuantityText(raw, uoms);
    return formatQuantityComposite(value, uom);
  };

  // Item 1 / item 6 (unit default from stated volume, widened 2026-07-26 to
  // goods/services/mixed - previously services-only): display-only default
  // for the quantity composite's unit dropdown, when echo_metadata's bound
  // intent states a non-calendar volume with a uom_master-matchable unit -
  // same "never written into the stored answer until the user changes
  // something" convention as defaultUomForType's goods "Each" default (see
  // QuantityCompositeInput in SmartAnswerInput.jsx). For goods/mixed this is
  // an inert backup - the prefill effect above already writes a real answer
  // from the same echo_metadata source, so this fallback chain never gets
  // reached once that has run; it only matters if that prefill couldn't run
  // for some reason. Looked up once here rather than per-question inside
  // the render loop below.
  const statedVolumeForQuantity = boundStatedVolume(echoMetadata);
  const statedUnitForQuantity = statedVolumeForQuantity
    ? matchStatedUnitAgainstMaster(statedVolumeForQuantity.rawUnitWord, uoms)
    : null;
  // Defect fix (2026-07-26): value and unit must default from the SAME
  // bound echo intent - previously only the unit reached the composite
  // (statedUnitForQuantity above); the stated NUMBER (quantities[0].value)
  // had no path to the value box at all for services, since the GOODS/
  // MIXED-only prefill effect above (gated `procurementType === 'services'`
  // -> return) never runs there. A services confirm-style question
  // ("You have indicated 2 SAP consultants...") rendered with its unit
  // pre-filled but the number empty, contradicting its own wording. For
  // goods/mixed this is the same inert-backup relationship
  // statedUnitForQuantity already has with the prefill effect above - a
  // real answer is written there first, so this value only ever matters
  // when nothing else has.
  const statedValueForQuantity = statedVolumeForQuantity?.value ?? null;

  // Filter to show only errors and warnings that block/warn users
  const issues = consistencyValidation?.issues || [];
  const errorIssues = issues.filter(i => i.severity === 'error');
  const warningIssues = issues.filter(i => i.severity === 'warning');

  // Item 1 (belt-and-suspenders read-time fallback, per Ashok's Q1 ruling):
  // a mandatory field_key should never be missing here - the generation-time
  // contract check (backend routes/procurement.py) already blocks a
  // malformed question set from ever persisting. If one still slips through,
  // this is a generation defect the user can't fix by answering more
  // carefully, so block with a Regenerate action instead of a cryptic
  // per-field error.
  //
  // Guarded to only fire when at least one recognized field_key is present
  // at all: a procurement created before this field_key migration shipped
  // has NONE (plain-string business_questions) - that's stale schema, not a
  // fresh generation defect, and the user can't fix it by regenerating
  // either (out of scope per the ticket - "full positional->field_key
  // migration... is a post-merge canonical refactor, NOT here"). Such a
  // procurement falls back to the pre-migration rendering (no mandatory
  // grouping, no block) exactly as it did before this batch.
  const missingMandatoryFieldKeys = useMemo(() => {
    const anyFieldKeyPresent = (businessQuestions || []).some((bq) => normalizeQuestion(bq).field_key);
    if (!anyFieldKeyPresent) return [];
    return findMissingMandatoryFieldKeys(businessQuestions, procurementType, boqPresent);
  }, [businessQuestions, procurementType, boqPresent]);

  useEffect(() => {
    if (missingMandatoryFieldKeys.length > 0) {
      postWizardEvent('answers', 'field_key_missing', { missing: missingMandatoryFieldKeys });
    }
  }, [missingMandatoryFieldKeys, postWizardEvent]);

  // LOCATION PRE-FILL (Phase 2 slice 3, 2026-08-06). {code, confidence}
  // or null. Passed to the ship_to_location control, which decides
  // whether it can actually be applied - see LocationDropdown's own
  // four-constraint comment. null and confidence "none" are treated
  // identically by that component: no pre-fill.
  const locationProposal = useMemo(
    () => getLocationProposal(parsedRequirement),
    [parsedRequirement],
  );

  // ONE SHARED ORDER (Ashok ruling 2026-09-03) - the same helper the
  // Questions step and the detail page render through. Numbers are display
  // positions 1..N; answers are still read and written by the STORED index
  // each item carries.
  const orderedQuestions = useMemo(
    () => orderQuestionsForDisplay(businessQuestions, procurementType, boqPresent),
    [businessQuestions, procurementType, boqPresent]
  );
  const mandatoryItems = useMemo(() => orderedQuestions.filter((it) => it.isMandatory), [orderedQuestions]);
  const optionalItems = useMemo(() => orderedQuestions.filter((it) => !it.isMandatory), [orderedQuestions]);
  const itemByIndex = useMemo(
    () => Object.fromEntries(orderedQuestions.map((it) => [it.index, it])),
    [orderedQuestions]
  );
  // Item 9 (2026-09-03): on a failed Continue, bring BOTH the summary and
  // the button row into view. Armed by the click, consumed once by the
  // effect below after the summary has rendered.
  const summaryRef = useRef(null);
  const actionRowRef = useRef(null);
  const bringIntoViewPendingRef = useRef(false);
  const unansweredMandatory = useMemo(
    () => findUnansweredMandatoryQuestions(businessQuestions, answers, procurementType),
    [businessQuestions, answers, procurementType]
  );
  // Listed in DISPLAY order (the order the buyer scrolls through), not
  // stored order - the number shown is the display number.
  const visibleMissing = useMemo(
    () => (showMissingMandatory
      ? [...unansweredMandatory].sort((a, b) => (itemByIndex[a.index]?.number ?? 0) - (itemByIndex[b.index]?.number ?? 0))
      : []),
    [showMissingMandatory, unansweredMandatory, itemByIndex]
  );
  const missingByIndex = useMemo(
    () => Object.fromEntries(visibleMissing.map((m) => [m.index, m.message])),
    [visibleMissing]
  );

  useEffect(() => {
    if (!bringIntoViewPendingRef.current || visibleMissing.length === 0) return;
    bringIntoViewPendingRef.current = false;
    // The row first (block: 'end' pins it to the bottom edge), then the
    // summary with the minimal scroll that makes it visible - both end up
    // on screen together, adjacency intact.
    if (actionRowRef.current && typeof actionRowRef.current.scrollIntoView === 'function') {
      actionRowRef.current.scrollIntoView({ block: 'end' });
    }
    if (summaryRef.current && typeof summaryRef.current.scrollIntoView === 'function') {
      summaryRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [visibleMissing.length]);

  // B2-B: resolved once, reused by the budget field's currency display
  // below (one fact, one source - never a second re-ask of currency).
  const currencyAnswerIndex = findAnswerByFieldKey(businessQuestions, answers, 'rfp_currency').index;

  const handleNextClick = () => {
    // PRESENCE FIRST, ALL FIELDS AT ONCE (Ruling 3, 2026-09-03). Every
    // unanswered mandatory question is reported together - under its own
    // control and in the summary above Continue - instead of one toast per
    // click for whichever check happened to run first. The toast is gone,
    // not duplicated: it was transient and top-center, and a buyer scrolled
    // to Continue could not read it. Format and membership checks below
    // keep their existing surfaces (out of this slice's scope).
    if (unansweredMandatory.length > 0) {
      bringIntoViewPendingRef.current = true;
      setShowMissingMandatory(true);
      return;
    }

    // Ship-to / delivery location - (when the location list loaded
    // successfully) must exist in the location master. Text-input
    // fallback (SmartAnswerInput's documented degraded path when the fetch
    // fails) is why this only enforces membership when locations actually loaded.
    const locationAnswer = findAnswerByFieldKey(businessQuestions, answers, 'ship_to_location');
    if (locationAnswer.index !== -1) {
      if (!loadingLocations && locations.length > 0) {
        const locId = (locationAnswer.value && typeof locationAnswer.value === 'object')
          ? locationAnswer.value.location_id
          : locationAnswer.value;
        if (!locations.some((loc) => loc.location_id === locId)) {
          toastError('Selected location is not recognized. Please choose a valid ship-to location.');
          return;
        }
      }
    }

    // Quantity - composite (number + unit-from-master), > 0 when answered.
    // Item A (services-engagement-models ruling): mandatory for goods/mixed,
    // OPTIONAL for services - a services engagement may have no natural
    // headline count at all. Whenever it IS answered, though, it must be a
    // complete composite regardless of type - a half-filled value (number
    // with no unit) is never valid. HEADLINE value only (same headline-vs-
    // per-line pattern as category): authoritative quantities are per-line
    // at Lines, so this never cross-checks against line items, which don't
    // exist yet at this step and, for multi-line/BOQ cases, never need to
    // reconcile to one number.
    const quantityAnswer = findAnswerByFieldKey(businessQuestions, answers, 'quantity');
    const quantityIsEmpty = quantityAnswer.index !== -1 && isAnswerEmpty(quantityAnswer.value);
    if (quantityAnswer.index !== -1) {
      // Presence (goods/mixed) is handled by the unansweredMandatory gate
      // above; an EMPTY optional services quantity is simply skipped here.
      if (!quantityIsEmpty) {
        // parseQuantityText (fuzzy), not a bare Number() (mirrors the budget
        // check below): belt-and-suspenders against a value that hasn't been
        // through the legacy-normalize/prefill effects yet (e.g. this same
        // render pass, right after a regenerate) - it's already a no-op
        // re-composition on an already-canonical "{number} {Unit}" value.
        const { value: qtyValue, uom: qtyUom } = parseQuantityText(quantityAnswer.value, uoms);
        const qtyNum = Number(qtyValue);
        if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
          toastError('Quantity must be a number greater than 0');
          return;
        }
        // Item 1a/1f: unit must be a real uom_master entry, same degraded-path
        // convention as location/currency above - only enforced once the
        // master has actually loaded, so a fetch failure never blocks
        // submission on a check it can't perform.
        if (!loadingUoms && uoms.length > 0) {
          if (!qtyUom) {
            toastError('Please select a unit for the quantity');
            return;
          }
          // Item B(ii) (duration/dates cooperation, services only): a
          // calendar-duration unit (Month/Week) describes a SPAN of the
          // engagement's own dates - if both exist and disagree with what
          // the dates imply, that's worth a confirm, not a silent submit.
          // Soft (non-blocking) - the user may genuinely intend the
          // mismatch (e.g. billing quarters vs. calendar months). Effort/
          // volume units (Hour, Engagement...) never trigger this.
          if (procurementType === 'services' && CALENDAR_DURATION_UNITS.includes(qtyUom)) {
            const startAnswer = findAnswerByFieldKey(businessQuestions, answers, 'start_date');
            const endAnswer = findAnswerByFieldKey(businessQuestions, answers, 'end_date');
            const derivedQty = deriveCalendarQuantityFromDates(startAnswer.value, endAnswer.value, qtyUom);
            if (derivedQty && derivedQty !== qtyNum) {
              // ATTRIBUTION + DE-DUP (item 3, stated-intent echo checks -
              // Ashok ruling 2026-07-25): if SARD would independently flag
              // the SAME fact (the typed quantity's value equals the
              // stated duration extracted from the requirement),
              // attribute this ONE dialog to the stated requirement
              // instead of the generic "Quantity says..." framing - never
              // both. The chain-based SARD check in runChecksAfterQuantity
              // is never reached this attempt either way, since this
              // block always returns below.
              const sardResult = checkStatedDurationEcho({
                echoMetadata,
                startIso: startAnswer.value,
                endIso: endAnswer.value,
              });
              const attributeToSard = sardAttributionForQuantityMismatch({
                qtyNum,
                qtyUom,
                statedDuration: sardResult ? { value: sardResult.statedValue, unit: sardResult.statedUnit } : null,
              });
              if (attributeToSard) {
                setQuantityDateMismatchDetails({ attributedToSARD: true, sard: sardResult });
              } else {
                setQuantityDateMismatchDetails({
                  attributedToSARD: false,
                  enteredText: `${qtyNum} ${qtyUom}${qtyNum === 1 ? '' : 's'}`,
                  datesText: `${formatDate(startAnswer.value)} – ${formatDate(endAnswer.value)}`,
                  derivedText: `${derivedQty} ${qtyUom}${derivedQty === 1 ? '' : 's'}`,
                });
              }
              setShowQuantityDateMismatch(true);
              return;
            }
          }
        }
      }
    }

    runChecksAfterQuantity();
  };

  // Everything from currency onward - split out of handleNextClick so the
  // quantity-vs-dates mismatch confirm dialog's "Proceed Anyway" can jump
  // straight here without re-running (and potentially re-triggering) the
  // quantity checks above.
  const runChecksAfterQuantity = () => {
    // Currency - mandatory, must exist in the currency master.
    const currencyAnswer = findAnswerByFieldKey(businessQuestions, answers, 'rfp_currency');
    if (currencyAnswer.index !== -1) {
      if (!loadingCurrencies && currencies.length > 0
        && !currencies.some((c) => c.currency_code === currencyAnswer.value)) {
        toastError('Selected currency is not recognized. Please choose a valid currency.');
        return;
      }
    }

    // Budget (item 5) - mandatory, numeric > 0. Inline (B2-B), not a
    // toast - see budgetError's declaration for why.
    const budgetAnswer = findAnswerByFieldKey(businessQuestions, answers, 'budget');
    if (budgetAnswer.index !== -1 && !isAnswerEmpty(budgetAnswer.value)) {
      const budgetNum = Number(parseLegacyBudget(budgetAnswer.value));
      if (!Number.isFinite(budgetNum) || budgetNum <= 0) {
        setBudgetError('Budget must be a number greater than 0');
        return;
      }
    }

    // Presence of every other mandatory question - the generic is_mandatory
    // catch-all, the required-by/start date, the services end date and the
    // submission deadline - is covered by the unansweredMandatory gate at
    // the top of handleNextClick (one predicate, one surface). What remains
    // here is the shared temporal check on the dates that ARE answered.
    const temporal = checkAnswersTemporalValidity({ businessQuestions, answers, procurementType });
    if (temporal.hardBlock) {
      // Item 3 (consolidated fix batch, 2026-07-24) - FIX: this used to
      // also call setTemporalBlock(temporal.hardBlock) here, which is what
      // caused the "A Date Has Passed" resume-narrative modal to wrongly
      // pop up during a LIVE edit (nothing "passed" - the user just typed
      // a value that violates a relationship rule like HVPN). temporalBlock
      // is now written ONLY by loadProcurement's resume check and the
      // gate's TEMPORAL_BLOCK catch (WizardContext.jsx) - a live submit-time
      // failure surfaces exclusively via this toast. One violation, one
      // surface.
      toastError(appendRuleCodeSuffix(temporal.hardBlock.message, temporal.hardBlock.code));
      return;
    }
    // Resolved (if a resume/gate-set temporalBlock was still showing) -
    // cleared the same way any other hard-validation failure clears, on
    // the next successful validation pass.
    setTemporalBlock(null);

    // Answers soft-warning acknowledgment fix (2026-07-25, Ashok's ruling):
    // soft alerts are evaluated FRESH on every submit click, forming this
    // attempt's chain - no per-procurement "already shown" guard survives
    // across submit attempts. "Proceed Anyway" acknowledges the current
    // warning and advances to the next in the chain (or submits, if it was
    // the last); "Go Back and Change Date" (or closing via X) aborts the
    // attempt entirely - the queued pendingChain below is simply never
    // read, and the next submit click re-evaluates everything from
    // scratch. This also means resume-time re-fire of a soft warning
    // (Principle 9) happens naturally: resume -> user submits -> fresh
    // evaluation, no explicit reset needed on load.
    //
    // SOFT-ALERT SEVERITY BATCH (2026-07-25/26): ordered most-severe-first
    // - SANT/SAST (HIGH, required date) before SAPN/SAPS (MEDIUM, runway)
    // before SAPT (LOW, today). requiredSoonGap is handled separately
    // (always shown first, unconditionally, when present) rather than
    // folded into the chain array, since nothing ever precedes it.
    const needsRequiredSoonWarning = Boolean(temporal.softWarnings.requiredSoonGap);
    const needsRunwayWarning = Boolean(temporal.softWarnings.runwayGap);
    const needsTodayWarning = Boolean(temporal.softWarnings.todayGap);

    // SARU (MIXED-SEMANTICS + ECHO FIX BATCH, item D) - ALL procurement
    // types, unlike SARV/SARD below: a cross-dimension unit pick (area vs
    // weight vs count vs time...) is a data-integrity concern regardless
    // of what's being procured. De-dup: when SARU fires, it is the more
    // specific/root-cause explanation of the same underlying fact a naive
    // SARV value-mismatch would also flag for the SAME answered quantity -
    // SARV is skipped in that case (see checkStatedUnitDimensionEcho's doc).
    const quantityAnswerForEcho = findAnswerByFieldKey(businessQuestions, answers, 'quantity');
    const saruResult = checkStatedUnitDimensionEcho({
      echoMetadata,
      answeredQuantityText: quantityAnswerForEcho.value,
      uoms,
    });

    // Stated-intent echo checks (Ashok ruling, 2026-07-25) - services only
    // (same scope as the existing typed-quantity-vs-dates check below,
    // and the ticket's own repro): SARD inherently needs start/end dates,
    // which only exist for services. SARV is scoped the same way here
    // deliberately, NOT because volume echo-checking is meaningless for
    // goods/mixed in principle, but because parsed_requirement.quantity
    // there routinely differs from the answered quantity as a matter of
    // ordinary refinement (e.g. a stated "10" the buyer later firms up to
    // "42" after clarifying) - a goods/mixed SARV would false-positive on
    // that everyday flow, unlike a services engagement's headline volume,
    // which the ticket's own evidence treats as a stable fact to echo
    // back. Both silent by default regardless (see
    // lib/statedIntentValidationRules.js's own guards) - a quantity/dates
    // answer that already triggered the EARLIER typed-quantity-vs-dates
    // dialog above never reaches here (that block always returns), so
    // there is no double-fire risk between the two.
    let sarvResult = null;
    let sardResult = null;
    if (procurementType === 'services') {
      sarvResult = saruResult ? null : checkStatedVolumeEcho({
        echoMetadata,
        answeredQuantityText: quantityAnswerForEcho.value,
        uoms,
      });
      const startAnswerForEcho = findAnswerByFieldKey(businessQuestions, answers, 'start_date');
      const endAnswerForEcho = findAnswerByFieldKey(businessQuestions, answers, 'end_date');
      sardResult = checkStatedDurationEcho({
        echoMetadata,
        startIso: startAnswerForEcho.value,
        endIso: endAnswerForEcho.value,
      });
    }

    // Rule-order after SAPN/SAPS, before SAPT (VALIDATION-FRAMEWORK.md).
    // SARU ordered right before SARV (see SOFT_ALERT_SEVERITY's CODE_ORDER).
    const chain = [];
    if (needsRunwayWarning) chain.push({ kind: 'runway', details: temporal.softWarnings.runwayGap });
    if (saruResult) chain.push({ kind: 'saru', details: saruResult });
    if (sarvResult) chain.push({ kind: 'sarv', details: sarvResult });
    if (sardResult) chain.push({ kind: 'sard', details: sardResult });
    if (needsTodayWarning) chain.push({ kind: 'today', details: temporal.softWarnings.todayGap });

    if (needsRequiredSoonWarning) {
      setRequiredSoonDetails(temporal.softWarnings.requiredSoonGap);
      setPendingChain(chain);
      setShowRequiredSoonWarning(true);
      return;
    }

    if (chain.length > 0) {
      const [first, ...rest] = chain;
      setPendingChain(rest);
      openChainItem(first);
      return;
    }

    handleAnswersSubmit();
  };

  // Shared by the first dispatch above and every soft-alert dialog's
  // "Proceed Anyway" (advanceChain below) - one switch, so a new chain
  // 'kind' only needs to be taught here once.
  const openChainItem = (item) => {
    if (item.kind === 'runway') {
      setRunwayDetails(item.details);
      setShowRunwayWarning(true);
    } else if (item.kind === 'saru') {
      setSaruDetails(item.details);
      setShowSaruWarning(true);
    } else if (item.kind === 'sarv') {
      setSarvDetails(item.details);
      setShowSarvWarning(true);
    } else if (item.kind === 'sard') {
      setSardDetails(item.details);
      setShowSardWarning(true);
    } else {
      setDeadlineSoonDetails(item.details);
      setShowDeadlineSoonWarning(true);
    }
  };

  // Shared by every soft-alert dialog's "Proceed Anyway" - shows the next
  // warning in pendingChain, or submits once the chain is exhausted.
  const advanceChain = () => {
    if (pendingChain.length === 0) {
      handleAnswersSubmit();
      return;
    }
    const [next, ...rest] = pendingChain;
    setPendingChain(rest);
    openChainItem(next);
  };

  // Item 1 fallback: a generation defect the user can't fix by answering -
  // block with Regenerate rather than surface a cryptic field_key error.
  if (!isReadOnly && missingMandatoryFieldKeys.length > 0) {
    return (
      <div className="relative">
        <GenerationLock active={loading} message="Regenerating your business questions — please wait" />
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">
            Answer Business Questions
          </h2>
          <p className="text-zinc-600">
            Provide answers to refine your procurement requirement.
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                We couldn't process these questions correctly
              </h4>
              <p className="text-sm text-red-700 mb-4">
                Some required questions are missing from this set. Please regenerate to continue.
              </p>
              <Button
                onClick={() => handleUploadsComplete()}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="regenerate-missing-field-key-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate Questions
              </Button>
            </div>
          </div>
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(1)} disabled={loading} className="border-zinc-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  // Casual review must be free and safe (spec §2.3): a completed step
  // revisited via Back renders read-only until the user explicitly opts in.
  if (isReadOnly) {
    return (
      <div className="relative">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">
            Answer Business Questions
          </h2>
          <p className="text-zinc-600">
            Provide answers to refine your procurement requirement.
          </p>
        </div>

        <ReadOnlyStepBanner stepId="answers" onEdit={() => unlockStepForEdit('answers', answers)} />

        <RequirementContextCard
          procurementId={procurementId}
          userRequirement={userRequirement}
          parsedRequirement={parsedRequirement}
        />

        <div className="space-y-4 mb-8">
          {[['required', mandatoryItems], ['optional', optionalItems]].map(([kind, items]) => (
            items.length === 0 ? null : (
              <div key={kind} className="space-y-4">
                <GroupHeading kind={kind} count={items.length} />
                {items.map(({ index: i, number, label }) => {
                  const saved = formatSavedAnswer(answers[`q${i}`]);
                  return (
                    <div key={i} data-testid={`answer-item-${i}`}>
                      <Label className="text-sm font-medium text-zinc-700 mb-2 block">
                        <span className="text-[#0055FF] mr-2">{number}.</span>
                        {label}
                      </Label>
                      <div className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2">
                        {saved || <span className="text-zinc-400 italic">No answer provided</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ))}
          <div>
            <Label className="text-sm font-medium text-zinc-700 mb-2 block">
              <span className="text-[#0055FF] mr-2">{businessQuestions.length + 1}.</span>
              {OTHER_SUGGESTION_QUESTION}
            </Label>
            <div className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 whitespace-pre-wrap">
              {formatSavedAnswer(answers[OTHER_SUGGESTION_KEY]) || <span className="text-zinc-400 italic">No answer provided</span>}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(1)} className="border-zinc-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={() => setCurrentStep(3)}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="continue-readonly-answers-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" /> Continue
          </Button>
        </div>
      </div>
    );
  }

  // One question, one cell. Called for every mandatory and optional item,
  // grouped or not; `number` and `label` come from the shared order.
  const renderAnswerItem = ({ index: i, isMandatory, number, label }) => {
    const question = businessQuestions[i];
    const nq = normalizeQuestion(question);
    const controlKind = resolveAnswerControl({ answerType: nq.answer_type, fieldKey: nq.field_key, suggestions: nq.suggestions });
    const width = answerLayoutWidth({ answerType: nq.answer_type, fieldKey: nq.field_key, suggestions: nq.suggestions });
    const missingMessage = missingByIndex[i] || null;
    return (
          <div
            key={i}
            id={`answer-item-${i}`}
            data-testid={`answer-item-${i}`}
            data-layout={width}
            className={width === 'full' ? 'md:col-span-2' : ''}
          >
            {isMandatory && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 mb-1 inline-block">
                MANDATORY
              </span>
            )}
            {/* The QUESTION TEXT spans its column (item 5) - only the control
                below is capped. Standard label for the canonical mandatory
                keys, the agent's own text otherwise (lib/questionDisplay). */}
            <Label className="text-sm font-medium text-zinc-700 mb-2 block">
              <span className="text-[#0055FF] mr-2">{number}.</span>
              {label}
            </Label>

            {/* CONTROL SIZED TO CONTENT (items 5 and 6): the wrapper, never
                the label, carries the cap - dates, currency and budget do
                not need a full column; location leaves room beside it for
                geographic_scope (C1b-2, unbuilt) and renders NOTHING there. */}
            <div
              className={controlWidthClass(controlKind)}
              data-testid={`answer-control-${i}`}
              data-control-width={controlWidthClass(controlKind) || 'full'}
            >

            {/* Currency question - dedicated dropdown, resolved by field_key
                (not text heuristic) now that generation reliably tags it. */}
            {nq.field_key === 'rfp_currency' ? (
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-zinc-400" />
                {loadingCurrencies ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading currencies...
                  </div>
                ) : (
                  <Select
                    value={answers[`q${i}`] || ''}
                    onValueChange={(val) => handleCurrencyChange(i, val)}
                  >
                    <SelectTrigger
                      className="w-full"
                      data-testid={`currency-select-${i}`}
                    >
                      <SelectValue placeholder="Select RFP currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem
                          key={currency.currency_code}
                          value={currency.currency_code}
                        >
                          {currency.currency_code} - {currency.currency_name}
                          {currency.symbol && ` (${currency.symbol})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ) : (
              <SmartAnswerInput
                value={
                  nq.field_key === 'budget' ? parseLegacyBudget(answers[`q${i}`])
                    : nq.field_key === 'quantity' ? displayQuantityValue(answers[`q${i}`])
                    : (answers[`q${i}`] || '')
                }
                onChange={(val) => {
                  setAnswers(prev => ({ ...prev, [`q${i}`]: val }));
                  markStepDirty('answers');
                  // B2-B: clear the moment the user acts, not just on the
                  // next Continue click - mirrors the BOQ upload message-
                  // lifecycle fix's "clear at the start of the new attempt".
                  if (nq.field_key === 'budget' && budgetError) setBudgetError(null);
                }}
                testId={`answer-input-${i}`}
                suggestions={nq.suggestions}
                answerType={nq.answer_type}
                fieldKey={nq.field_key}
                procurementType={procurementType}
                statedUnit={nq.field_key === 'quantity' ? statedUnitForQuantity : undefined}
                statedValue={nq.field_key === 'quantity' ? statedValueForQuantity : undefined}
                // B2-B: budget renders currency for context - the
                // currency question (rule 5) is answered earlier in this
                // same list, never re-asked here (one fact, one source).
                currencyCode={nq.field_key === 'budget' ? (answers[`q${currencyAnswerIndex}`] || '') : undefined}
                // LOCATION PRE-FILL (Phase 2 slice 3, 2026-08-06). The
                // proposal travels down; the membership check against
                // the ACTIVE options happens inside LocationDropdown,
                // against the very list it renders.
                locationProposal={nq.field_key === 'ship_to_location' ? locationProposal : undefined}
                // A pre-fill is a SYSTEM action, not a buyer edit, so it
                // writes the answer WITHOUT markStepDirty. Marking dirty
                // would raise the unsaved-changes guard on a step the
                // buyer has not touched; and nothing is at risk if they
                // navigate away, since the pre-fill is recomputed from
                // the persisted proposal on return.
                onLocationPrefill={nq.field_key === 'ship_to_location'
                  ? (val) => setAnswers(prev => ({ ...prev, [`q${i}`]: val }))
                  : undefined}
              />
            )}
            </div>
            {/* Point of use (Ruling 3): the presence message under the
                control it names; budget's format error shares the slot. */}
            <FieldError message={(nq.field_key === 'budget' && budgetError) || missingMessage} />
            {/* Item 4: the budget note leaves the question body (the label
                replaced it) for a footnote under the control. */}
            {nq.field_key === 'budget' && (
              <p className="text-xs text-zinc-500 mt-1" data-testid="budget-footnote">{BUDGET_FOOTNOTE}</p>
            )}
          </div>
    );
  };

  return (
    <div className="relative">
      <GenerationLock active={loading} message="Validating your answers — please wait" />
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-zinc-900 mb-2">
          Answer Business Questions
        </h2>
        <p className="text-zinc-600">
          Provide answers to refine your procurement requirement.
        </p>
      </div>

      <RequirementContextCard
        procurementId={procurementId}
        userRequirement={userRequirement}
        parsedRequirement={parsedRequirement}
      />

      {/* Item 7: persistent blocking banner for a temporal hard block (set
          by WizardContext on resume or by the RFP-generation gate) - stays
          up regardless of the one-time explaining dialog's open/closed
          state, and clears itself once handleNextClick's next pass finds
          the dates valid again. */}
      {temporalBlock && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="temporal-block-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-1">
                A date needs updating
              </h4>
              <p className="text-sm text-red-700">
                {temporalBlock.message}{' '}
                <span className="text-red-400 text-xs">{formatRuleCodeSuffix(temporalBlock.code)}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Consistency Validation Errors - Blocking */}
      {errorIssues.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Please correct the following errors before proceeding:
              </h4>
              <ul className="space-y-2">
                {errorIssues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-red-700">
                    <span className="font-medium">{issue.field}:</span> {issue.message}
                    {issue.suggestion && (
                      <div className="text-red-600 mt-1 text-xs">{issue.suggestion}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Consistency Validation Warnings - Non-blocking */}
      {warningIssues.length > 0 && errorIssues.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">
                Please review the following warnings:
              </h4>
              <ul className="space-y-2">
                {warningIssues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-amber-700">
                    <span className="font-medium">{issue.field}:</span> {issue.message}
                    {issue.suggestion && (
                      <div className="text-amber-600 mt-1 text-xs">{issue.suggestion}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6 mb-8">
        {/* TWO GROUPS, TWO COLUMNS (Rulings 1 and 2, 2026-09-03). Each group
            is a 2-column grid from `md` up: a SHORT control (picker or
            number - answerLayoutWidth, decided by answer type and field
            key, never question text) takes one cell; a FULL one (free
            text, or anything unrecognised) spans both. Short items pair up
            by grid auto-flow; a full item ends its row. Below `md` it is
            one column. Three columns were measured and refused: ~352px per
            column in this container against the quantity composite's
            ~300px minimum. */}
        {[['required', mandatoryItems], ['optional', optionalItems]].map(([kind, items]) => (
          items.length === 0 ? null : (
            <div key={kind}>
              <GroupHeading kind={kind} count={items.length} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-testid={`question-group-${kind}`}>
                {/* RELATED FIELDS UNDER ONE HEADING (item 4): consecutive
                    items matching a FIELD_GROUP render inside one framed
                    block spanning the row; everything else is a cell. */}
                {segmentOrderedQuestions(items).map((segment) => (
                  segment.kind === 'group' ? (
                    <fieldset
                      key={`group-${segment.group.id}`}
                      className="md:col-span-2 border border-zinc-200 rounded-lg p-4"
                      data-testid={`field-group-${segment.group.id}`}
                    >
                      <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        {segment.group.heading}
                      </legend>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {segment.items.map(renderAnswerItem)}
                      </div>
                    </fieldset>
                  ) : renderAnswerItem(segment.item)
                ))}
              </div>
            </div>
          )
        ))}

        {/* Issue 19 - non-mandatory, not a business question (see
            OTHER_SUGGESTION_KEY comment above). Numbered N+1 against the
            STORED count, like every question above. */}
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">
            <span className="text-[#0055FF] mr-2">{businessQuestions.length + 1}.</span>
            {OTHER_SUGGESTION_QUESTION}
          </Label>
          <Textarea
            ref={autoResizeTextarea}
            rows={TEXTAREA_BASE_ROWS}
            className={GROWING_TEXTAREA_CLASS}
            value={answers[OTHER_SUGGESTION_KEY] || ''}
            onChange={(e) => {
              autoResizeTextarea(e.target);
              setAnswers(prev => ({ ...prev, [OTHER_SUGGESTION_KEY]: e.target.value }));
              markStepDirty('answers');
            }}
            placeholder="Optional - anything else vendors or the RFP should account for"
            data-testid="answer-input-other-suggestion"
          />
        </div>
      </div>

      {/* WHERE THE ACTION IS (Ruling 3, 2026-09-03). Rendered as the
          element IMMEDIATELY BEFORE the Back/Continue row, so a buyer whose
          click just failed is looking at it. Each entry is a jump link to
          the field; jumping changes no state, so this stays rendered when
          they scroll back. It disappears only when the list is empty -
          i.e. when every required question is answered. */}
      {visibleMissing.length > 0 && (
        <div
          ref={summaryRef}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
          role="alert"
          data-testid="missing-mandatory-summary"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 mb-2">
                Answer {visibleMissing.length === 1 ? 'this required question' : `these ${visibleMissing.length} required questions`} before continuing
              </h4>
              <ul className="space-y-1">
                {visibleMissing.map((m) => (
                  <li key={m.index} className="text-sm text-red-700">
                    <button
                      type="button"
                      className="underline underline-offset-2 text-left hover:text-red-900"
                      onClick={() => jumpToQuestion(m.index)}
                      data-testid={`missing-mandatory-link-${m.index}`}
                    >
                      {itemByIndex[m.index]?.number ?? m.index + 1}. {itemByIndex[m.index]?.label ?? m.question}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between" data-testid="answers-action-row" ref={actionRowRef}>
        <Button variant="outline" onClick={() => setCurrentStep(1)} disabled={loading} className="border-zinc-200">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={handleNextClick}
          disabled={loading}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
          data-testid="submit-answers-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          {loading ? 'Validating...' : 'Continue'}
        </Button>
      </div>

      {/* Item 7: one-time explaining dialog for a temporal hard block -
          opens once per arrival (see the effect above); the persistent
          banner near the top stays up independently of this dialog's
          open/closed state.
          Item 3 (consolidated fix batch, 2026-07-24): the narrative frame
          depends on WHICH rule triggered this - a genuinely past date
          (HVPT/HVND/HVSD) reads as "a date has passed" (true - resume
          detected wall-clock time moving past it); a relationship rule
          (HVPN/HVPS/HVES) is framed neutrally instead, since nothing is
          literally in the past - the dates just no longer relate to each
          other correctly (e.g. a line was edited elsewhere, or the
          submission-to-required gap collapsed). This dialog is ONLY ever
          shown for a resume/gate-set temporalBlock now (see handleNextClick
          above) - a live edit never reaches it. */}
      <Dialog open={showTemporalBlockDialog} onOpenChange={setShowTemporalBlockDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isPastDateTemporalBlock ? 'A Date Has Passed' : 'Dates Need Attention'}</DialogTitle>
            <DialogDescription>
              {isPastDateTemporalBlock ? (
                <>
                  Since you last worked on this procurement, a date has passed. {temporalBlock?.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(temporalBlock?.code)}</span> Update it below to
                  continue.
                </>
              ) : (
                <>
                  This procurement's dates need attention before continuing: {temporalBlock?.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(temporalBlock?.code)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={() => setShowTemporalBlockDialog(false)}
              data-testid="acknowledge-temporal-block-btn"
            >
              Update Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRequiredSoonWarning} onOpenChange={setShowRequiredSoonWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* Soft-alert severity batch (2026-07-26): SANT/SAST surfaced at
                Answers - highest severity (HIGH), always first in the
                chain when it fires. Same amber-family + chip + muted-
                suffix pattern as the other two soft-alert dialogs. */}
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Required Date Is Very Close
              <SeverityChip severity={SOFT_ALERT_SEVERITY[requiredSoonDetails?.code] || SOFT_ALERT_SEVERITY.SANT} />
            </DialogTitle>
            <DialogDescription>
              {requiredSoonDetails ? (
                <>
                  {requiredSoonDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(requiredSoonDetails.code)}</span>
                </>
              ) : (
                'The required date is less than 7 days from today - this compresses the entire downstream cycle. Do you want to proceed anyway?'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowRequiredSoonWarning(false)}>
              Go Back and Change Date
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowRequiredSoonWarning(false); advanceChain(); }}
              data-testid="confirm-required-soon-warning-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeadlineSoonWarning} onOpenChange={setShowDeadlineSoonWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* SOFT-ALERT SEVERITY BATCH (2026-07-25): amber-family styling
                (icon + chip) for every soft-alert decision dialog - these
                request a risk acknowledgment, not a neutral confirmation
                (blue stays reserved for that; red for blocking errors).
                2026-07-26: SAPT is LOW severity - no chip renders for it
                (SeverityChip returns null for LOW), just the plain title +
                muted rule-code suffix below. */}
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Submission Deadline Is Close
              <SeverityChip severity={SOFT_ALERT_SEVERITY[deadlineSoonDetails?.code] || SOFT_ALERT_SEVERITY.SAPT} />
            </DialogTitle>
            {/* Consolidated fix batch (2026-07-24), item 1: renders the
                shared engine's own message directly (dates/gap embedded
                there) instead of hand-rolling the sentence here - fixing
                the copy once in dateValidationRules.js now reaches this
                dialog automatically. Item 2: the rule code renders as a
                separately-styled muted span (dialogs get muted styling,
                per the ticket) rather than being concatenated into the
                plain message text. */}
            <DialogDescription>
              {deadlineSoonDetails ? (
                <>
                  {deadlineSoonDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(deadlineSoonDetails.code)}</span>
                </>
              ) : (
                'The submission deadline is less than 7 days from today. Vendors may not have sufficient time to prepare and submit proposals. Do you want to proceed anyway?'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeadlineSoonWarning(false)}>
              Go Back and Change Date
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowDeadlineSoonWarning(false); advanceChain(); }}
              data-testid="confirm-deadline-soon-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRunwayWarning} onOpenChange={setShowRunwayWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* Consolidated fix batch (2026-07-24), item 1: this dialog's
                title/body previously described SAPT's concept (vendor
                response time), not SAPN/SAPS's - a confirmed content bug.
                Title + body now match the master-table Message 3 language,
                rendered directly from the shared engine (see the Warning A
                dialog's comment above for why). */}
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Timeline May Be Too Tight
              <SeverityChip severity={SOFT_ALERT_SEVERITY[runwayDetails?.code] || SOFT_ALERT_SEVERITY.SAPN} />
            </DialogTitle>
            <DialogDescription>
              {runwayDetails ? (
                <>
                  {runwayDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(runwayDetails.code)}</span>
                </>
              ) : (
                'The gap between the submission deadline and the required date is less than 7 days - this may not be sufficient for completing downstream activities. Do you want to proceed anyway?'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowRunwayWarning(false)}>
              Go Back and Change Date
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowRunwayWarning(false); advanceChain(); }}
              data-testid="confirm-runway-warning-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item B(ii) (duration/dates cooperation, services only): quantity
          (Month/Week) vs. the engagement's own start/end dates disagree -
          soft confirm, not a hard block. "Proceed Anyway" jumps straight to
          runChecksAfterQuantity, same continuation the quantity block
          itself would have called with no mismatch. */}
      <Dialog open={showQuantityDateMismatch} onOpenChange={setShowQuantityDateMismatch}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* Attribution + de-dup (item 3, stated-intent echo checks -
                Ashok ruling 2026-07-25): when the typed quantity's value
                equals the stated duration extracted from the requirement,
                this is SARD's own fact - anchor the wording to the stated
                requirement (never "Quantity says...") and show SARD's
                chip/code, not the generic framing. The generic (non-
                attributed) branch is rule code SAQD (Stated-Answer
                Quantity/Dates - the pre-existing typed-quantity-vs-dates
                check, formalized with its own code per the code-in-
                message audit: every soft-alert message must carry its
                rule code, this dialog was the known miss). */}
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {quantityDateMismatchDetails?.attributedToSARD ? 'Stated Duration and Dates Don\'t Quite Match' : "Quantity and Dates Don't Quite Match"}
              <SeverityChip severity={quantityDateMismatchDetails?.attributedToSARD ? SOFT_ALERT_SEVERITY.SARD : SOFT_ALERT_SEVERITY.SAQD} />
            </DialogTitle>
            <DialogDescription>
              {quantityDateMismatchDetails?.attributedToSARD ? (
                <>
                  {quantityDateMismatchDetails.sard.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix('SARD')}</span>
                </>
              ) : quantityDateMismatchDetails ? (
                <>
                  Quantity says <span className="font-medium">{quantityDateMismatchDetails.enteredText}</span>, but
                  the engagement dates ({quantityDateMismatchDetails.datesText}) work out to approximately{' '}
                  <span className="font-medium">{quantityDateMismatchDetails.derivedText}</span>. Confirm which is
                  intended.{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix('SAQD')}</span>
                </>
              ) : (
                "The quantity and the engagement's start/end dates don't quite line up. Confirm which is intended."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowQuantityDateMismatch(false)} data-testid="edit-quantity-date-mismatch-btn">
              Let Me Check
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowQuantityDateMismatch(false); runChecksAfterQuantity(); }}
              data-testid="confirm-quantity-date-mismatch-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SARU (MIXED-SEMANTICS + ECHO FIX BATCH, item D) - cross-dimension
          unit echo, ALL procurement types. Same per-attempt chain/
          proceed-anyway convention as every other soft-alert dialog. */}
      <Dialog open={showSaruWarning} onOpenChange={setShowSaruWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Unit Changed Across Dimensions
              <SeverityChip severity={SOFT_ALERT_SEVERITY.SARU} />
            </DialogTitle>
            <DialogDescription>
              {saruDetails ? (
                <>
                  {saruDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(saruDetails.code)}</span>
                </>
              ) : (
                "Your initial stated requirement's unit doesn't match the type of unit you've selected. Confirm which is intended."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowSaruWarning(false)}>
              Let Me Check
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowSaruWarning(false); advanceChain(); }}
              data-testid="confirm-saru-warning-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stated-intent echo checks (Ashok ruling, 2026-07-25) - SARV
          (volume) and SARD (duration), evaluated independently of the
          dialog above (which only covers the calendar-quantity-vs-dates
          case and always returns before reaching this chain). Same
          per-attempt chain/proceed-anyway convention as every other
          soft-alert dialog. */}
      <Dialog open={showSarvWarning} onOpenChange={setShowSarvWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Stated Quantity and Answered Quantity Don't Quite Match
              <SeverityChip severity={SOFT_ALERT_SEVERITY.SARV} />
            </DialogTitle>
            <DialogDescription>
              {sarvDetails ? (
                <>
                  {sarvDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(sarvDetails.code)}</span>
                </>
              ) : (
                "Your initial stated requirement's quantity doesn't quite match what you've answered. Confirm which is intended."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowSarvWarning(false)}>
              Let Me Check
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowSarvWarning(false); advanceChain(); }}
              data-testid="confirm-sarv-warning-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSardWarning} onOpenChange={setShowSardWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Stated Duration and Dates Don't Quite Match
              <SeverityChip severity={SOFT_ALERT_SEVERITY.SARD} />
            </DialogTitle>
            <DialogDescription>
              {sardDetails ? (
                <>
                  {sardDetails.message}{' '}
                  <span className="text-zinc-400 text-xs">{formatRuleCodeSuffix(sardDetails.code)}</span>
                </>
              ) : (
                "Your initial stated requirement's duration doesn't quite match your engagement dates. Confirm which is intended, or update your dates."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowSardWarning(false)}>
              Let Me Check
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowSardWarning(false); advanceChain(); }}
              data-testid="confirm-sard-warning-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showConsistencyWarning} onOpenChange={setShowConsistencyWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Please Review Warnings</DialogTitle>
            <DialogDescription>
              {(consistencyValidation?.issues || []).filter(i => i.severity === 'warning').length} warning(s) were found in your answers. Review them, or proceed anyway.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm text-amber-700 max-h-60 overflow-y-auto">
            {(consistencyValidation?.issues || [])
              .filter(i => i.severity === 'warning')
              .map((issue, idx) => (
                <li key={idx}>
                  <span className="font-medium">{issue.field}:</span> {issue.message}
                </li>
              ))}
          </ul>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowConsistencyWarning(false)}>
              Go Back and Review
            </Button>
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={() => { setShowConsistencyWarning(false); confirmAdvanceToLineItems(); }}
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StepAnswers;
